import { Platform } from 'react-native';
import * as Location from 'expo-location';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';
import { apiService, VisitPhoto } from '../services/api';

export interface CapturedPosition {
  latitude: number;
  longitude: number;
  accuracyMeters?: number;
}

/** Device GPS fix — expo-location on native, the browser API on web. */
/** maxAgeMs: how old a cached fix may be (web only; 0 = always a fresh one). */
export async function getCurrentPosition(opts?: { maxAgeMs?: number }): Promise<CapturedPosition> {
  if (Platform.OS === 'web') {
    return new Promise((resolve, reject) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) return reject(new Error('Location is not available in this browser'));
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracyMeters: p.coords.accuracy }),
        (e) => reject(new Error(e.message || 'Location permission denied')),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: opts?.maxAgeMs ?? 10000 }
      );
    });
  }
  const { status } = await Location.requestForegroundPermissionsAsync();
  if (status !== 'granted') throw new Error('Location permission denied');
  const p = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High });
  return { latitude: p.coords.latitude, longitude: p.coords.longitude, accuracyMeters: p.coords.accuracy ?? undefined };
}

export const haversineMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000;
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** Web: a file input with capture="environment" opens the rear camera on
 * phones (desktop browsers fall back to a file chooser). */
function pickWebImage(cameraOnly: boolean): Promise<{ base64: string; name: string } | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (cameraOnly) input.setAttribute('capture', 'environment');
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || '');
        resolve({ base64: dataUrl.split(',')[1] || '', name: file.name || `visit-${Date.now()}.jpg` });
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    };
    input.click();
  });
}

/**
 * Takes (or, when the visit allows it, picks) one photo and uploads it —
 * returns the VisitPhoto to send with start/end, or null when cancelled.
 */
export async function captureVisitPhoto(opts: {
  phase: 'start' | 'end';
  cameraOnly: boolean;
  businessId: string;
  position?: CapturedPosition | null;
}): Promise<VisitPhoto | null> {
  let base64: string;
  let fileName: string;
  if (Platform.OS === 'web') {
    const picked = await pickWebImage(opts.cameraOnly);
    if (!picked?.base64) return null;
    base64 = picked.base64;
    fileName = picked.name;
  } else {
    const permission = opts.cameraOnly
      ? await ImagePicker.requestCameraPermissionsAsync()
      : await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) throw new Error('Camera permission denied');
    const result = opts.cameraOnly
      ? await ImagePicker.launchCameraAsync({ mediaTypes: 'images', quality: 0.8 })
      : await ImagePicker.launchImageLibraryAsync({ mediaTypes: 'images', quality: 0.8 });
    if (result.canceled || !result.assets?.[0]) return null;
    const asset = result.assets[0];
    base64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' });
    fileName = asset.fileName || `visit-${opts.phase}-${Date.now()}.jpg`;
  }
  const uploaded = await apiService.uploadMediaToStorage({
    fileName,
    fileData: base64,
    assetType: 'image',
    businessId: opts.businessId,
    campaignName: 'visits',
  });
  return {
    url: uploaded.url,
    mediaId: uploaded.mediaId,
    capturedAt: new Date().toISOString(),
    coordinates: opts.position ? { latitude: opts.position.latitude, longitude: opts.position.longitude } : undefined,
    phase: opts.phase,
  } as VisitPhoto;
}
